import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, DimApiError, DimNetworkError, sessionToken, type AutoMeasureResult, type ImageLine } from '../api/dim';
import { requestWakeLock } from '../capture/camera';
import { DEFAULT_THRESHOLDS } from '../capture/gates';
import { requestMotionPermission } from '../capture/imu';
import type { OrientationLimits } from '../capture/orientation';
import type { CaptureResult } from '../capture/session';
import { useCaptureSession } from '../capture/useCaptureSession';
import type { Vec3 } from '../fsm/machine';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// /misura — la misura senza tocchi, Dim separata da Quick. Si entra con l'utente di EMS, si sceglie il bancale (80, 100 o 120 × 120), si inquadra e si
// scatta DENTRO la pagina: la foto parte insieme alla gravità letta in quell'istante, e il server prende la verticale dal sensore invece di
// stimarla dalla foto (dall'alto le linee del piano superiore ingannano la stima). L'app fotocamera non lo permetterebbe: foto e sensore
// sarebbero di due momenti diversi.
// Disegno: sistema iOS di Quick in modalità scura. Pensato per la pistola: 320 × 405, guanti, rumore. L'unico numero grande è l'altezza,
// l'unico valore misurato. Tre bancali (80, 100 e 120 × 120) fotografati dal lato corto, e un numero ogni volta che è sensato, con gli avvisi
// sotto: la tolleranza chiesta è ±10 cm.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * I bancali che si misurano, fotografati dal lato corto (il 120 × 120 da un lato qualsiasi): quello è il fronte, la profondità (120) la dà il
 * server. Base e volume vengono da qui.
 */
const PALLETS = [
  { frontMm: 800, label: '80 × 120', hint: 'Bancale 80 × 120 intero, dal lato da 80' },
  { frontMm: 1000, label: '100 × 120', hint: 'Bancale 100 × 120 intero, dal lato da 100' },
  { frontMm: 1200, label: '120 × 120', hint: 'Bancale 120 × 120 intero' },
] as const;
type Pallet = (typeof PALLETS)[number];
const it = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2, minimumFractionDigits: 2 });

/** Gli stessi limiti del server (AutoMeasureSteps): oltre 35° in su o in giù la lente non è calibrata, di lato la pistola va tenuta dritta. */
const LIMITS: OrientationLimits = { maxPitchDownDeg: 35, maxPitchUpDeg: 35, maxRollDeg: 8 };

type Phase =
  | { kind: 'login'; notice?: string }
  | { kind: 'ready'; notice?: string }
  | { kind: 'measuring' }
  | { kind: 'result'; result: AutoMeasureResult };

/** Lo scatto mandato al server: i byte, l'indirizzo per mostrarlo e la gravità di quell'istante. */
interface Shot {
  blob: Blob;
  url: string;
  gravity: Vec3 | null;
}

function problemText(e: unknown): string {
  if (e instanceof DimApiError) return e.detail;
  if (e instanceof DimNetworkError) return 'Nessuna risposta dal server. Controlla la rete e riprova.';
  return 'Qualcosa è andato storto. Riprova.';
}

export function AutoMeasurePage() {
  const [phase, setPhase] = useState<Phase>(() => (sessionToken.get() ? { kind: 'ready' } : { kind: 'login' }));
  const [shot, setShot] = useState<Shot | null>(null);
  // Si riparte sempre da 80 × 120: un tipo rimasto scelto dalla volta prima sbaglierebbe il numero del 25 % senza che nessuno se ne accorga.
  const [pallet, setPallet] = useState<Pallet>(PALLETS[0]);

  // L'indirizzo della foto vive quanto lo scatto: si libera quando arriva il prossimo o la pagina si chiude.
  useEffect(() => (shot ? () => URL.revokeObjectURL(shot.url) : undefined), [shot]);

  /** La foto da misurare e la gravità del suo istante: null per una foto scelta fra quelle già fatte (la verticale la danno le linee). */
  const measure = (blob: Blob, gravity: Vec3 | null): void => {
    setShot({ blob, url: URL.createObjectURL(blob), gravity });
    setPhase({ kind: 'measuring' });
    api
      .autoMeasure(blob, pallet.frontMm, gravity)
      .then((result) => setPhase({ kind: 'result', result }))
      .catch((e: unknown) => {
        if (e instanceof DimApiError && e.status === 401) {
          sessionToken.clear();
          setPhase({ kind: 'login', notice: 'Sessione scaduta. Accedi di nuovo.' });
          return;
        }
        setPhase({ kind: 'ready', notice: problemText(e) });
      });
  };

  /** La sessione si chiude anche sul server; se la rete manca, il token si toglie comunque dalla scheda. */
  const signOut = (): void => {
    void api.logout().finally(() => setPhase({ kind: 'login' }));
  };

  if (phase.kind === 'login') return <SignIn notice={phase.notice} onSignedIn={() => setPhase({ kind: 'ready' })} />;

  if (phase.kind === 'measuring') return <Measuring />;

  if (phase.kind === 'result') {
    return <Result result={phase.result} photoUrl={shot?.url ?? null} onNew={() => setPhase({ kind: 'ready' })} />;
  }

  return <Viewfinder notice={phase.notice} pallet={pallet} onPallet={setPallet} onShot={measure} onSignOut={signOut} />;
}

// ── Misura in corso ──────────────────────────────────────────────────────────────────────────────────────────

/** Una misura può prendere decine di secondi: i secondi che scorrono dicono che non è ferma, e lo schermo resta acceso fino alla risposta. */
function Measuring() {
  const seconds = useElapsedSeconds();
  useScreenAwake();
  return (
    <Page title="Misura bancale">
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-riemp border-t-blu motion-reduce:animate-none" />
        <p className="text-[17px] font-semibold">Sto misurando</p>
        <p className="text-[15px] tabular-nums text-sec">{seconds} s</p>
      </div>
    </Page>
  );
}

function useElapsedSeconds(): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const id = setInterval(() => setSeconds(Math.floor((performance.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  return seconds;
}

/**
 * Schermo acceso finché il componente c'è. Con lo schermo spento Chrome sospende la pagina e la risposta della misura va persa. Il blocco cade
 * da solo quando la pagina si nasconde (tasto di accensione, cambio di app): al ritorno si richiede. Senza l'API (o negata) si va avanti senza.
 */
function useScreenAwake(): void {
  useEffect(() => {
    let release: (() => void) | undefined;
    let done = false;
    const acquire = (): void => {
      if (document.visibilityState !== 'visible') return;
      void requestWakeLock().then((r) => {
        if (done) r();
        else release = r;
      });
    };
    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      done = true;
      document.removeEventListener('visibilitychange', acquire);
      release?.();
    };
  }, []);
}

// ── Inquadratura ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * La fotocamera dentro la pagina: si apre quando la vista compare e si chiude quando sparisce (anche durante la misura, per la batteria).
 * Il tasto si accende solo con la pistola ferma e dentro i limiti di inclinazione: una foto mossa legge male la gravità, una troppo inclinata
 * esce dalla zona calibrata, e in entrambi i casi il server la rifiuterebbe dopo dieci secondi di attesa.
 */
function Viewfinder({
  notice,
  pallet,
  onPallet,
  onShot,
  onSignOut,
}: {
  notice?: string;
  pallet: Pallet;
  onPallet: (p: Pallet) => void;
  onShot: (blob: Blob, gravity: Vec3 | null) => void;
  onSignOut: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const scratchRef = useRef<HTMLCanvasElement>(null);
  const cap = useCaptureSession(videoRef, scratchRef, { thresholds: DEFAULT_THRESHOLDS, limits: LIMITS });
  useMotionOnFirstTap();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(notice);
  const live = cap.live;
  const steady = !!live && live.ring >= 1;
  const ready = steady && !live.orientationHint && !busy;
  // Una frase sola, la più importante: prima la fotocamera, poi la posizione, poi la stabilità, poi la luce.
  const guidance = cap.error
    ? 'La fotocamera non si apre: controlla il permesso o usa una foto.'
    : !live
      ? 'Apro la fotocamera'
      : (live.orientationHint?.hint ?? (!steady ? 'Tieni ferma la fotocamera' : (live.quality?.hint ?? pallet.hint)));

  const shoot = (): void => {
    if (!ready) return;
    setBusy(true);
    setProblem(undefined);
    cap
      .shoot()
      // La foto ferma a piena risoluzione se il browser l'ha data, altrimenti il fotogramma del video: il server sceglie la lente dalla risoluzione.
      .then((c: CaptureResult) => onShot(c.photo ?? c.blob, c.gravity))
      .catch((e: unknown) => {
        setProblem(e instanceof Error ? e.message : 'Lo scatto non è riuscito. Riprova.');
        setBusy(false);
      });
  };

  return (
    <Page
      center={<PalletPicker value={pallet} onChange={onPallet} disabled={busy} />}
      trailing={
        <button
          type="button"
          onClick={onSignOut}
          disabled={busy}
          className="min-h-[44px] px-2 text-[17px] text-blu active:opacity-70 disabled:text-ter focus-visible:outline focus-visible:outline-2 focus-visible:outline-blu"
        >
          Esci
        </button>
      }
      footer={
        <div className="flex gap-2">
          {/* Una foto già fatta (galleria della pistola, file del PC): senza sensore, la verticale la stimano le linee della foto. */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="min-h-[50px] shrink-0 rounded-[14px] bg-riemp px-4 text-[17px] font-semibold text-blu active:opacity-70 disabled:text-ter focus-visible:outline focus-visible:outline-2 focus-visible:outline-blu"
          >
            Usa una foto
          </button>
          <Action onClick={shoot} disabled={!ready}>
            Misura
          </Action>
        </div>
      }
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onShot(f, null);
        }}
      />
      {problem && <Notice>{problem}</Notice>}
      {/* Il riquadro ha la forma della foto (3:4, in verticale): si vede tutto quello che viene scattato. Più largo della foto, tagliava sopra e
          sotto e il bancale finiva più piccolo del necessario nella foto vera. La guida sta sotto, a tutta larghezza: sopra copriva la base. */}
      <div className="relative min-h-[160px] flex-1">
        <div className="absolute inset-y-0 left-1/2 aspect-[3/4] max-w-full -translate-x-1/2 overflow-hidden rounded-2xl bg-black">
          <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-contain" aria-label="Inquadratura della fotocamera" />
          <canvas ref={scratchRef} className="hidden" />
          <div className="pointer-events-none absolute inset-3 rounded-xl border border-white/35" aria-hidden="true" />
        </div>
      </div>
      <p
        role="status"
        className={`flex min-h-[46px] items-center justify-center rounded-xl px-3 py-1.5 text-center text-[15px] font-semibold leading-snug ${
          ready ? 'bg-verde text-black' : 'bg-riemp text-testo'
        }`}
      >
        {guidance}
      </p>
    </Page>
  );
}

/**
 * Il sensore di movimento si usa da solo dove c'è. Su iPhone iOS lo concede solo se la richiesta parte da un tocco dell'utente: la si fa al
 * primo tocco sulla pagina, qualunque sia, e iOS mostra la sua domanda. Dove i dati arrivano già (Android, la pistola) non si chiede niente;
 * senza sensore, o se l'utente dice no, la verticale la danno le linee della foto.
 */
function useMotionOnFirstTap(): void {
  useEffect(() => {
    const api = (globalThis as { DeviceMotionEvent?: { requestPermission?: unknown } }).DeviceMotionEvent;
    if (typeof api?.requestPermission !== 'function') return undefined;
    let flowing = false;
    const onMotion = (e: DeviceMotionEvent): void => {
      if (e.accelerationIncludingGravity?.x != null) flowing = true;
    };
    const onTap = (): void => {
      document.removeEventListener('click', onTap, true);
      if (!flowing) void requestMotionPermission();
    };
    window.addEventListener('devicemotion', onMotion);
    document.addEventListener('click', onTap, true);
    return () => {
      window.removeEventListener('devicemotion', onMotion);
      document.removeEventListener('click', onTap, true);
    };
  }, []);
}

// ── Accesso ──────────────────────────────────────────────────────────────────────────────────────────────────

function SignIn({ notice, onSignedIn }: { notice?: string; onSignedIn: () => void }) {
  const [codSoc, setCodSoc] = useState('');
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(notice);
  // Il mondo dall'indirizzo (/misura?mondo=cavotec); senza, Premi.
  const iss = new URLSearchParams(location.search).get('mondo') ?? 'premi';
  const complete = codSoc.trim().length === 3 && user.trim().length > 0 && password.length > 0;

  const submit = (): void => {
    if (!complete || busy) return;
    setBusy(true);
    setProblem(undefined);
    api
      .login(iss, codSoc.trim(), user.trim(), password)
      .then(onSignedIn)
      .catch((e: unknown) => setProblem(problemText(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Page
      footer={
        <Action onClick={submit} disabled={!complete || busy}>
          {busy ? 'Accesso in corso' : 'Accedi'}
        </Action>
      }
    >
      <div className="flex items-center gap-3 pb-1 pt-2">
        <Mark size={44} />
        <div>
          <p className="text-[22px] font-bold leading-tight tracking-tight">Dim</p>
          <p className="text-[14px] text-sec">Misura i bancali con una foto</p>
        </div>
      </div>
      {problem && <Notice>{problem}</Notice>}
      <div className="flex flex-col gap-3">
        <Input label="Società" value={codSoc} onChange={setCodSoc} inputMode="numeric" maxLength={3} autoFocus />
        <Input label="Utente" value={user} onChange={setUser} autoComplete="username" />
        <Input label="Password" value={password} onChange={setPassword} type="password" autoComplete="current-password" onEnter={submit} />
      </div>
    </Page>
  );
}

// ── Risultato ────────────────────────────────────────────────────────────────────────────────────────────────

function Result({ result: r, photoUrl, onNew }: { result: AutoMeasureResult; photoUrl: string | null; onNew: () => void }) {
  if (!r.shown) {
    return (
      <Page title="Foto da rifare" footer={<Action onClick={onNew}>Rifai la foto</Action>}>
        <div className="flex flex-col gap-2">
          {r.refuseReasons.map((reason) => (
            <Notice key={reason}>{reason}</Notice>
          ))}
        </div>
        {photoUrl && <Annotated result={r} photoUrl={photoUrl} />}
      </Page>
    );
  }

  const cm = (mm: number): number => Math.round(mm / 10);
  return (
    <Page title="Misura bancale" footer={<Action onClick={onNew}>Nuova misura</Action>}>
      <section className="rounded-2xl bg-scheda px-4 pb-4 pt-3 shadow-scheda">
        <p className="text-[15px] text-sec">Altezza da terra</p>
        <p className="text-[56px] font-bold leading-[1.05] tracking-tight tabular-nums">
          {cm(r.heightMm ?? 0)}
          <span className="ml-1 text-[28px] font-semibold text-sec">cm</span>
        </p>
        {r.method === 'stima' && <p className="text-[14px] text-sec">Stimata dalla larghezza del bancale</p>}
        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-sep pt-3">
          <Figure label="Bancale" value={`${cm(r.frontMm)} × ${cm(r.depthMm)} cm`} />
          <Figure label="Volume" value={`${it.format(r.volumeM3 ?? 0)} m³`} />
        </div>
      </section>
      {r.warnings.length > 0 && <ToCheck items={r.warnings} />}
      {photoUrl && <Annotated result={r} photoUrl={photoUrl} />}
      <p className="text-[14px] text-sec">Questa misura non viene salvata.</p>
    </Page>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[13px] text-sec">{label}</p>
      <p className="text-[17px] font-semibold tabular-nums">{value}</p>
    </div>
  );
}

/** La foto con le tre linee trovate: rosso la cima, giallo il piano del bancale, blu il pavimento. */
function Annotated({ result, photoUrl }: { result: AutoMeasureResult; photoUrl: string }) {
  const line = (l: ImageLine | null, color: string) =>
    l && <line x1={l.a.x} y1={l.a.y} x2={l.b.x} y2={l.b.y} stroke={color} strokeWidth={3} strokeLinecap="round" vectorEffect="non-scaling-stroke" />;
  return (
    <figure className="relative overflow-hidden rounded-2xl bg-black">
      <img src={photoUrl} alt="La foto del bancale con le linee della misura" className="block max-h-[46dvh] w-full object-contain" />
      <svg viewBox={`0 0 ${result.imageWidthPx} ${result.imageHeightPx}`} preserveAspectRatio="xMidYMid meet" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
        {line(result.top, '#FF453A')}
        {line(result.deck, '#FFD60A')}
        {line(result.floor, '#0A84FF')}
      </svg>
    </figure>
  );
}

// ── Pezzi ────────────────────────────────────────────────────────────────────────────────────────────────────

function Page({
  title,
  center,
  trailing,
  children,
  footer,
}: {
  title?: string;
  center?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col bg-fondo">
      {center ? (
        <header className="sticky top-0 z-10 flex h-11 shrink-0 items-center gap-1 border-b border-sep bg-fondo/90 pl-2 pr-1 backdrop-blur">
          <div className="min-w-0 flex-1">{center}</div>
          {trailing}
        </header>
      ) : (
        title && (
          <header className="sticky top-0 z-10 flex h-11 shrink-0 items-center justify-center border-b border-sep bg-fondo/90 px-4 backdrop-blur">
            <h1 className="text-[17px] font-semibold">{title}</h1>
            {trailing && <div className="absolute inset-y-0 right-2 flex items-center">{trailing}</div>}
          </header>
        )
      )}
      <main className="flex flex-1 flex-col gap-3 px-4 py-3">{children}</main>
      {footer && <footer className="sticky bottom-0 border-t border-sep bg-fondo/90 px-4 py-3 backdrop-blur">{footer}</footer>}
    </div>
  );
}

function Action({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="min-h-[50px] w-full rounded-[14px] bg-blu text-[17px] font-semibold text-white transition-opacity active:opacity-70 disabled:bg-riemp disabled:text-ter focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blu"
    >
      {children}
    </button>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
  inputMode,
  maxLength,
  autoComplete,
  autoFocus,
  onEnter,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'password';
  inputMode?: 'numeric' | 'text';
  maxLength?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  onEnter?: () => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="px-1 text-[13px] text-sec">{label}</span>
      <input
        className="min-h-[46px] rounded-[12px] bg-scheda px-3 text-[17px] text-testo caret-blu outline-none ring-1 ring-inset ring-sep focus:ring-2 focus:ring-blu"
        type={type}
        value={value}
        inputMode={inputMode}
        maxLength={maxLength}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onEnter?.()}
      />
    </label>
  );
}

/** I problemi che non impediscono il numero, in un riquadro solo: il titolo in arancione, le righe in testo normale e separate. */
function ToCheck({ items }: { items: string[] }) {
  return (
    <section className="rounded-2xl bg-scheda px-4 py-3 shadow-scheda">
      <p className="flex items-center gap-2 text-[15px] font-semibold text-arancio-t">
        <span className="h-2 w-2 rounded-full bg-arancio" aria-hidden="true" />
        Da controllare
      </p>
      <ul className="mt-1 divide-y divide-sep">
        {items.map((w) => (
          <li key={w} className="py-2 text-[14px] leading-snug">
            {w}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Il segno di Dim: un bancale visto di fronte (carico, piano, tre blocchi) con la quota dell'altezza accanto. Bianco sul blu di sistema, linee
 * piene e niente sfumature: lo stesso disegno fa da icona del sito (public/icona.svg).
 */
function Mark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" className="shrink-0">
      <rect width="64" height="64" rx="14" className="fill-blu" />
      <g fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <rect x="14" y="15" width="25" height="26" rx="2" />
        <path d="M11 45h31" />
        <path d="M51 15v30M47.5 15h7M47.5 45h7" />
      </g>
      <g fill="#fff">
        <rect x="12" y="46.5" width="6" height="5" rx="1" />
        <rect x="23.5" y="46.5" width="6" height="5" rx="1" />
        <rect x="35" y="46.5" width="6" height="5" rx="1" />
      </g>
    </svg>
  );
}

/** Il bancale, nella barra in alto: controllo segmentato come in iOS, tre parti uguali. Il fronte è il lato corto, quello da fotografare. */
function PalletPicker({ value, onChange, disabled }: { value: Pallet; onChange: (p: Pallet) => void; disabled?: boolean }) {
  return (
    <div role="radiogroup" aria-label="Bancale" className="flex w-full rounded-[9px] bg-riemp p-0.5">
      {PALLETS.map((p) => {
        const on = p.frontMm === value.frontMm;
        return (
          <button
            key={p.frontMm}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(p)}
            className={`min-h-[34px] flex-1 whitespace-nowrap rounded-[7px] px-1 text-[14px] tabular-nums text-testo focus-visible:outline focus-visible:outline-2 focus-visible:outline-blu ${
              on ? 'bg-segmento font-semibold' : 'font-normal'
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="flex gap-2 rounded-[12px] bg-rosso/10 px-3 py-2.5 text-[15px] leading-snug text-rosso-t">
      <svg width="18" height="18" viewBox="0 0 24 24" className="mt-px shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7.5v5.5M12 16.5v.01" />
      </svg>
      <span>{children}</span>
    </div>
  );
}
